import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetAdminReferralsQuery } from "store/api/referralsApi"
import { UITypes } from "types"
import { TableActionButtonGroup } from "types/table/tableActionButton"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconEdit } from "assets/svg/icon-edit.svg"

import { convertVendorReferralAgentsToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { ButtonNew } from "components/form/ButtonNew"
import { CheckboxNew } from "components/form/CheckboxNew"
import TableWrapper from "components/table/TableWrapper"

import { EditReferralModal } from "./EditReferralModal"
import { getReferralsTableHeaderCells, referralsTableHeaderCells } from "./referralsTableHeaderCells"
import { ReferralTrends } from "./ReferralTrends"

// the backend sorts agent groups by plain "name"/"email", while the filter params use the agent_ prefix
const SORT_COLUMN_MAPPING: Record<string, string> = {
  agent_name: "name",
  agent_email: "email",
}

export const Referrals = () => {
  const [showArchive, setShowArchive] = useState(false)
  const [editableReferral, setEditableReferral] = useState<{ referralId: number; partnerName: string } | null>(null)

  const [triggerGetAdminReferrals, { isFetching, data }] = useLazyGetAdminReferralsQuery()
  const { urlQueryParams } = useUrlQueryParams()

  const agents = data?.data

  const getReferrals = useCallback(async () => {
    try {
      const { orderColumn, ...params } = urlQueryParams
      const res = await triggerGetAdminReferrals({
        ...params,
        ...(orderColumn ? { orderColumn: SORT_COLUMN_MAPPING[orderColumn] ?? orderColumn } : {}),
        ...(showArchive ? { status: "denied", exclude_denied: false } : {}),
      })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }, [showArchive, urlQueryParams, triggerGetAdminReferrals])

  useEffect(() => {
    getReferrals()
  }, [getReferrals])

  const getRowActions = useCallback(
    (rowData: UITypes.TableBodyRow): TableActionButtonGroup => ({
      actionButtons: [
        {
          icon: <IconEdit />,
          tooltip: "Edit",
          hidden: !!rowData.subRows,
          handler: id => {
            const agent = agents?.find(({ referrals }) => referrals.some(referral => referral.id === id))
            if (agent) setEditableReferral({ referralId: id, partnerName: agent.name })
          },
        },
      ],
    }),
    [agents],
  )

  const visibleAgents = showArchive
    ? agents?.map(agent => ({ ...agent, referrals: agent.referrals.filter(({ status }) => status === "denied") }))
    : agents

  const tableRows = convertVendorReferralAgentsToTableRows(visibleAgents, showArchive)

  const { handleLocalExport } = useExportCSV({
    params: {},
    headerCells: referralsTableHeaderCells,
    fileName: "referrals",
  })

  return (
    <>
      <ReferralTrends />
      <ContentWrapper title="Referrals">
        <nav className={styles.tableNavWrapper}>
          <div />
          <div className={classNames(styles.tableNavButtonsWrapper)}>
            <CheckboxNew
              className={styles.tableNavItem}
              isReverse={true}
              id="archive"
              onChange={event => setShowArchive(event.target.checked)}
            >
              Show Archive
            </CheckboxNew>
            <ButtonNew
              className={styles.tableNavItem}
              onClick={() => handleLocalExport(tableRows)}
              disabled={!tableRows.length}
            >
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          headerCells={getReferralsTableHeaderCells(showArchive)}
          data={tableRows}
          isLoading={isFetching}
          rowsExpanded={true}
          getRowActions={showArchive ? undefined : getRowActions}
          expandRowOnClick
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {editableReferral && (
        <EditReferralModal
          referralId={editableReferral.referralId}
          partnerName={editableReferral.partnerName}
          onCancel={() => setEditableReferral(null)}
          onSuccess={() => setEditableReferral(null)}
        />
      )}
    </>
  )
}
